import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PageHeader from "../../shared/components/PageHeader";

const termLabel = (term) =>
  term ? `${term.name}${term.is_semester_term ? " (semester)" : ""}` : "";

const gradeLabel = (level) => (level === "all" ? "All grades" : level);

// Compact chip styling for the header selects that sit beside the title: no fixed
// width (they size to the selected value), tighter padding/font than size="small",
// and the arrow pulled to the edge with the right padding set to just clear it.
const compactSelectSx = {
  flexShrink: 0,
  fontSize: 13,
  "& .MuiSelect-select": { py: 0.5, pl: 1, pr: "22px" },
  "& .MuiSelect-icon": { right: 0 },
};

// Quick Find field: the in-progress text is local (a form draft kept off the
// URL); it is applied on submit/clear, when v1 writes the query param. Keyed by
// the applied query in StudentsHeader so an external reset reseeds it.
function StudentsSearchField({ query, onSearch }) {
  const [value, setValue] = useState(query ?? "");

  const submit = (e) => {
    e.preventDefault();
    onSearch(value.trim());
  };

  const clear = () => {
    setValue("");
    onSearch("");
  };

  return (
    <form onSubmit={submit}>
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Quick Find"
        variant="standard"
        size="small"
        sx={{ width: { xs: "100%", md: 220 } }}
        slotProps={{
          input: {
            "aria-label": "Quick Find",
            endAdornment: (
              <InputAdornment position="end">
                {value ? (
                  <IconButton
                    aria-label="Clear search"
                    onClick={clear}
                    edge="end"
                  >
                    <ClearRoundedIcon />
                  </IconButton>
                ) : (
                  <IconButton aria-label="Search" type="submit" edge="end">
                    <SearchRoundedIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
    </form>
  );
}

// Page-header definition for the legacy v1 Students page. The page has no tabs;
// its controls - Quick Find, the term picker, the grade-level menu, the
// show-all/my-students toggle, and the count - all move into the right-aligned
// actions slot here. Data + callbacks cross the v1/v2 bridge; v1 keeps owning the
// term/grade/query params and the student fetch they drive.
export default function StudentsHeader({
  query,
  onSearch,
  terms = [],
  selectedTermId,
  onTermChange,
  gradeLevels = [],
  gradeLevel,
  onGradeLevelChange,
  manageAllPermission,
  onlyMyStudents,
  myStudentsCount,
  totalStudents,
  allStudentsCount,
  showShowAllToggle,
  onToggleShowAll,
}) {
  const termValue = terms.some((t) => t.id === selectedTermId)
    ? selectedTermId
    : "";

  return (
    <PageHeader
      title={
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}
        >
          <Typography
            sx={{ fontSize: 24, lineHeight: 1.2, whiteSpace: "nowrap" }}
          >
            Students
          </Typography>

          <Select
            size="small"
            value={termValue}
            onChange={(e) => onTermChange(e.target.value)}
            displayEmpty
            inputProps={{ "aria-label": "Term" }}
            renderValue={(v) =>
              termLabel(terms.find((t) => t.id === v)) || "Select term"
            }
            sx={compactSelectSx}
          >
            {terms.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {termLabel(t)}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={gradeLevel ?? "all"}
            onChange={(e) => onGradeLevelChange(e.target.value)}
            inputProps={{ "aria-label": "Grade level" }}
            renderValue={(v) => gradeLabel(v)}
            sx={compactSelectSx}
          >
            {gradeLevels.map((level) => (
              <MenuItem key={level} value={level}>
                {gradeLabel(level)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      }
      actions={
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "center", justifyContent: "flex-end" }}
        >
          {showShowAllToggle && (
            <Button
              onClick={onToggleShowAll}
              size="small"
              sx={{
                flexShrink: 0,
                whiteSpace: "nowrap",
                textTransform: "none",
              }}
            >
              {onlyMyStudents ? "Show all" : "Show my students"}
            </Button>
          )}

          <Box sx={{ whiteSpace: "nowrap", flexShrink: 0, fontSize: 14 }}>
            {manageAllPermission && (
              <>
                <Typography component="span" sx={{ fontWeight: 500 }}>
                  {onlyMyStudents ? myStudentsCount : totalStudents}
                </Typography>{" "}
                of{" "}
              </>
            )}
            <Typography component="span" sx={{ fontWeight: 500 }}>
              {allStudentsCount}
            </Typography>{" "}
            students
          </Box>

          <StudentsSearchField key={query} query={query} onSearch={onSearch} />
        </Stack>
      }
    />
  );
}
